import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@/components/ui/Button";
import QueryStateWrapper from "@/components/ui/QueryStateWrapper";
import { useCart } from "@/hooks/useCart";
import {
  useCloseSession,
  useCurrentSession,
  useSessionInvoices,
} from "@/hooks/useSession";
import { toast } from "react-toastify";
import { clearTokens } from "@/utils/token";
import ConfirmationModal from "@/components/ui/ConfirmationModal";

const SessionSales = () => {
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const { data: sessionData, isLoading: isLoadingSession } =
    useCurrentSession();
  const {
    data: sessionInvoicesData,
    isLoading: isLoadingInvoices,
    isFetching: isFetchingInvoices,
    refetch: refetchInvoices,
  } = useSessionInvoices();
  const { mutate: closeAllSessions } = useCloseSession();

  const [total, setTotal] = useState(0);
  const [cashTotal, setCashTotal] = useState(0);
  const [cardTotal, setCardTotal] = useState(0);

  // حالة نافذة التأكيد
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  /* -----------  Pagination State ------------- */
  const [currentPage, setCurrentPage] = useState(1);
  const invoicesPerPage = 10;

  // إعادة تعيين الصفحة الأولى عند تغيّر قائمة الفواتير
  useEffect(() => {
    setCurrentPage(1);
  }, [sessionInvoicesData]);

  useEffect(() => {
    if (!sessionInvoicesData) return;
    // لا تصفّر أثناء التحميل/الجلب؛ حدّث القيم بسلاسة
    const invoices = (sessionInvoicesData as any)?.invoices || [];
    let t = 0;
    let cash = 0;
    let card = 0;
    invoices.forEach((invoice: any) => {
      const invoiceTotal = (invoice.cash || 0) + (invoice.visa || 0);
      t += invoiceTotal;
      cash += invoice.cash || 0;
      card += invoice.visa || 0;
    });
    setTotal(t);
    setCashTotal(cash);
    setCardTotal(card);
  }, [sessionInvoicesData]);

  // تنفيذ الإغلاق الفعلي بعد التأكيد
  const confirmEndSession = () => {
    const sid = (sessionData as any)?.id;
    if (!sid) return;
    closeAllSessions(sid, {
      onSuccess: () => {
        clearCart();
        localStorage.removeItem("sessionStarted");
        localStorage.removeItem("lastQROperation");
        clearTokens();
        navigate("/login");
        toast.success("تم إنهاء الجلسة بنجاح");
      },
      onError: () => {
        toast.error("فشل في إنهاء الجلسة، حاول مرة أخرى");
      },
    });
  };

  // عند الضغط على زر الإغلاق، أظهر نافذة التأكيد
  const handleEndSession = () => {
    setShowConfirmModal(true);
  };

  // نجعل فحص الجلسة يتم في الخلفية بدون إظهار تحميل للمستخدم
  const isLoading = isLoadingInvoices && !sessionInvoicesData; // اعرض الكاش إن وُجد حتى أثناء الجلب
  const invoices = (sessionInvoicesData as any)?.invoices || [];
  const s = sessionData as any;

  /* -----------  Pagination Logic ------------- */
  const totalPages = Math.ceil(invoices.length / invoicesPerPage) || 1;
  const paginatedInvoices = invoices.slice(
    (currentPage - 1) * invoicesPerPage,
    currentPage * invoicesPerPage
  );

  const sessionStatus = s
    ? {
        id: s.id,
        isActive: !s.ended_at, // الجلسة النشطة هي التي بدون ended_at
        createdAt: s.created_at,
        endedAt: s.ended_at,
      }
    : null;

  // فحص إذا كانت الجلسة منتهية أو غير موجودة → توجيه إلى صفحة بدء الجلسة بدون إزعاج المستخدم
  useEffect(() => {
    const sessionStarted = localStorage.getItem("sessionStarted");
    // إذا انتهى التحميل ولم توجد جلسة نشطة (بعد أن كانت بدأت محلياً)
    if (!isLoadingSession && !sessionData && sessionStarted) {
      // إشعار بسيط دون إظهار شاشات تحميل مزعجة
      toast.error("لا توجد جلسة نشطة، ابدأ جلسة جديدة");
      navigate("/start-session");
      return;
    }

    // إذا كانت الجلسة موجودة لكنها منتهية (بعد أن كانت بدأت محلياً)
    if (s && s.ended_at && sessionStarted) {
      toast.error("انتهت الجلسة، ابدأ جلسة جديدة");
      navigate("/start-session");
      return;
    }
  }, [s, isLoadingSession, navigate]);

  return (
    <QueryStateWrapper isLoading={isLoading} isError={false}>
      <div className='p-4 space-y-6'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-3'>
            <h2 className='text-2xl font-bold text-right'>مبيعات الجلسة</h2>
            <span className='px-2 py-1 rounded-full text-xs bg-red-100 text-red-700 border border-red-200'>
              عدد الفواتير: {invoices.length}
            </span>
          </div>
          <div className='flex items-center gap-2'>
            <Button
              type='primary'
              onClick={() => refetchInvoices()}
              className='px-3 py-2 text-sm'
            >
              تحديث
            </Button>
            {isFetchingInvoices && (
              <span className='text-xs text-gray-500'>جارٍ التحديث...</span>
            )}
          </div>
        </div>

        {/* معلومات الجلسة */}
        {sessionStatus && (
          <div className='text-right text-sm space-y-1'>
            {sessionStatus.isActive && (
              <span className='text-green-600 font-bold'> (نشطة ومفتوحة)</span>
            )}
            {!sessionStatus.isActive && (
              <span className='text-red-600 font-bold'> (مغلقة)</span>
            )}
            <p className='text-gray-500'>
              بدأت في:{" "}
              {new Date(sessionStatus.createdAt).toLocaleString("ar-SA")}
            </p>
            {sessionStatus.endedAt && (
              <p className='text-gray-500'>
                انتهت في:{" "}
                {new Date(sessionStatus.endedAt).toLocaleString("ar-SA")}
              </p>
            )}
          </div>
        )}

        <p className='text-right text-gray-500'>ملخص الجلسة</p>

        {/* التحقق من وجود جلسة نشطة */}
        {!sessionData ? (
          <div className='text-center py-8 space-y-4'>
            <div className='text-red-500 text-lg font-bold'>
              لا توجد جلسة حالياً
            </div>
            <p className='text-gray-500'>يرجى بدء جلسة جديدة لعرض الفواتير</p>
            <div className='text-sm text-gray-400'>
              تأكد من وجود جلسة نشطة ومفتوحة لعرض بياناتها
            </div>
          </div>
        ) : s?.ended_at ? (
          <div className='text-center py-8 space-y-4'>
            <div className='text-red-500 text-lg font-bold'>الجلسة مغلقة</div>
            <p className='text-gray-500'>
              يرجى بدء جلسة جديدة لعرض فواتير الجلسة النشطة
            </p>
            <div className='text-sm text-gray-400'>
              الجلسة الحالية (#{s.id}) مغلقة في{" "}
              {new Date(s.ended_at).toLocaleString("ar-SA")}
            </div>
          </div>
        ) : invoices.length === 0 ? (
          <p className='text-center text-gray-600 mt-10'>
            لا توجد فواتير في الجلسة النشطة حتى الآن
          </p>
        ) : (
          <div className='space-y-4'>
            {paginatedInvoices.map((invoice: any, index: number) => (
              <div
                key={invoice.id || index}
                onClick={() => navigate(`/invoices/${invoice.id}`)}
                className='border-2 border-red-300 rounded-[20px] p-4 flex justify-between items-center hover:bg-red-50 text-right cursor-pointer'
              >
                <div className='flex flex-col space-y-1 text-lg text-gray-800 text-right'>
                  <p className='font-bold'>فاتورة {invoice.id}</p>
                  <p>رقم الجوال: {invoice.phone || "غير محدد"}</p>
                  <p>عدد الأصناف: {invoice.order?.items?.length || 0}</p>
                  <p className='text-red-600 font-bold'>
                    السعر الكلي:{" "}
                    {((invoice.cash || 0) + (invoice.visa || 0)).toFixed(2)}{" "}
                    ريال
                  </p>
                </div>
                <div className='text-sm text-gray-500 space-y-1 text-center'>
                  <div className='text-xs text-gray-600'>
                    الساعة
                    {new Date(
                      invoice.order?.created_at || invoice.created_at
                    ).toLocaleTimeString("ar-SA", {
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true,
                    })}
                  </div>
                  <div className='text-xs text-red-600'>
                    {new Date(
                      invoice.order?.created_at || invoice.created_at
                    ).toLocaleDateString("ar-SA")}
                    م
                  </div>
                </div>
              </div>
            ))}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className='flex justify-center items-center gap-2 mt-4'>
                <button
                  className={`px-3 py-1 rounded border text-sm font-bold  ${
                    currentPage === 1
                      ? "cursor-not-allowed text-gray-400 border-gray-300"
                      : "text-red-600 border-red-600 hover:bg-red-50"
                  }`}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  السابق
                </button>
                <span>
                  صفحة {currentPage} من {totalPages}
                </span>
                <button
                  className={`px-3 py-1 rounded border text-sm font-bold  ${
                    currentPage === totalPages
                      ? "cursor-not-allowed text-gray-400 border-gray-300"
                      : "text-red-600 border-red-600 hover:bg-red-50"
                  }`}
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages}
                >
                  التالي
                </button>
              </div>
            )}
          </div>
        )}

        <div className='text-right space-y-2 mt-6'>
          <div>
            <p className='text-gray-700'>عدد فواتير الجلسة</p>
            <p className='text-red-600 font-bold text-xl'>{invoices.length}</p>
          </div>
          <div>
            <p className='text-gray-700'>المبلغ الكلي</p>
            <p className='text-red-600 font-bold text-xl'>
              {total.toFixed(2)} ريال
            </p>
          </div>
          <hr className='border-red-100' />
          <div className='flex justify-between gap-6'>
            <div>
              <p className='text-gray-700'>مبلغ نقدي</p>
              <p className='text-red-600 font-bold'>
                {cashTotal.toFixed(2)} ريال
              </p>
            </div>
            <div>
              <p className='text-gray-700'>مبلغ شبكة</p>
              <p className='text-red-600 font-bold'>
                {cardTotal.toFixed(2)} ريال
              </p>
            </div>
          </div>
        </div>

        <div className='mx-auto'>
          <Button
            type='primary'
            onClick={handleEndSession}
            className='text-xl mt-6 w-full'
            disabled={!s || !!s?.ended_at}
          >
            {!s || !!s?.ended_at
              ? "لا توجد جلسة نشطة لإغلاقها"
              : "اغلاق الجلسة"}
          </Button>
        </div>

        {/* نافذة التأكيد */}
        <ConfirmationModal
          show={showConfirmModal}
          title='تأكيد إغلاق الجلسة'
          message='هل أنت متأكد أنك تريد إغلاق الجلسة؟'
          confirmText='تأكيد الإغلاق'
          cancelText='إلغاء'
          onConfirm={() => {
            setShowConfirmModal(false);
            confirmEndSession();
          }}
          onCancel={() => setShowConfirmModal(false)}
        />

        {/* لا توجد Pagination بعد إلغاء التقسيم */}
      </div>
    </QueryStateWrapper>
  );
};

export default SessionSales;
